/* eslint-disable react/no-unused-prop-types */
import { ReactNode, useCallback, useMemo } from 'react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Option, RenderIf } from 'react-rainbow-components';
import { FileText, Send, CheckCircle2 } from 'lucide-react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import useStoredValue from 'data/hooks/useStoredValue';
import { InvoiceCreateStatus } from '../types';
import {
    StatusOptionContainer,
    StatusOptionCopy,
    StatusOptionDescription,
    StatusOptionLabel,
    StatusPicklistGlobalStyle,
    StyledPicklist,
} from './styled';

interface StatusPickerOptionProps {
    label?: string;
    description?: string;
    icon?: ReactNode;
}

const StatusPickerOption = ({
    label,
    description,
    icon,
}: StatusPickerOptionProps) => (
    <StatusOptionContainer>
        <RenderIf isTrue={Boolean(icon)}>
            {icon}
        </RenderIf>
        <StatusOptionCopy>
            <StatusOptionLabel>{label}</StatusOptionLabel>
            <RenderIf isTrue={Boolean(description)}>
                <StatusOptionDescription>{description}</StatusOptionDescription>
            </RenderIf>
        </StatusOptionCopy>
    </StatusOptionContainer>
);

const LAST_INVOICE_STATUS_KEY = 'invoice-create-status';

const isInvoiceCreateStatus = (value: unknown): value is InvoiceCreateStatus => (
    value === 'draft' || value === 'sent' || value === 'paid'
);

export const useLastInvoiceCreateStatus = () => {
    const { get, save } = useStoredValue<InvoiceCreateStatus>(LAST_INVOICE_STATUS_KEY);

    const getLastStatus = useCallback((): InvoiceCreateStatus => {
        const stored = get();
        return isInvoiceCreateStatus(stored) ? stored : 'draft';
    }, [get]);

    const saveLastStatus = useCallback((status: InvoiceCreateStatus) => {
        if (isInvoiceCreateStatus(status)) {
            save(status);
        }
    }, [save]);

    return { getLastStatus, saveLastStatus };
};

const statusOptions = [
    {
        name: 'draft',
        label: 'Draft',
        description: 'Not sent to customer',
        value: 'draft',
        icon: <FileText size={16} />,
    },
    {
        name: 'sent',
        label: 'Sent',
        description: 'Sent to customer',
        value: 'sent',
        icon: <Send size={16} />,
    },
    {
        name: 'paid',
        label: 'Paid',
        description: 'Payment received',
        value: 'paid',
        icon: <CheckCircle2 size={16} />,
    },
];

interface StatusPickerProps {
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    value?: InvoiceCreateStatus;
    onChange?: (value: InvoiceCreateStatus) => void;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    remember?: boolean;
}

const StatusPicker = (props: StatusPickerProps) => {
    const { remember = true, ...formProps } = props;
    const {
        id,
        className,
        name,
        label,
        value: valueInProps,
        onChange = () => {},
        error,
        placeholder,
        required,
    } = useReduxForm(formProps);
    const { saveLastStatus } = useLastInvoiceCreateStatus();

    const pickListValue = useMemo(
        () => statusOptions.find((option) => option.value === valueInProps),
        [valueInProps],
    );

    const handleOnChange = useCallback(
        (next: PicklistValue) => {
            const status = next.value as InvoiceCreateStatus;
            if (remember) saveLastStatus(status);
            onChange(status);
        },
        [onChange, remember, saveLastStatus],
    );

    return (
        <>
            <StatusPicklistGlobalStyle />
            <StyledPicklist
                id={id}
                name={name}
                className={className}
                label={label}
                labelAlignment="left"
                value={pickListValue}
                placeholder={placeholder}
                onChange={handleOnChange}
                required={required}
                error={error}
            >
                <Option variant="header" label="Select status" />
                {statusOptions.map((option) => (
                    <Option
                        key={option.name}
                        component={StatusPickerOption}
                        name={option.name}
                        label={option.label}
                        value={option.value}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        description={option.description}
                        icon={option.icon}
                    />
                ))}
            </StyledPicklist>
        </>
    );
};

export default StatusPicker;
